import { supabase } from './supabase';
import { resolveCurrentSender, senderColumns } from './messagesCore';

export interface TwilioSettings {
  businessId: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  callForwardingEnabled?: boolean;
  forwardingNumber?: string;
  resourcePhoneNumbers?: string[];
  enabled: boolean;
}


/**
 * Formats a phone number to E.164 format
 */
export function formatToE164(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Add + prefix if not present
  if (!phoneNumber.startsWith('+')) {
    // Assume US number if no country code
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    }
    return `+${cleaned}`;
  }
  
  return phoneNumber;
}

// Track ongoing fetches to prevent concurrent calls for same business
const ongoingFetches = new Map<string, Promise<void>>();

/**
 * Fetches new messages from Twilio and stores them in the database
 */
export async function fetchAndStoreMessages(settings: {
  businessId: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
}): Promise<void> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return;
  }

  // Prevent concurrent fetches for the same business
  const fetchKey = `${settings.businessId}-${settings.phoneNumber}`;
  if (ongoingFetches.has(fetchKey)) {
    console.log('⏳ Fetch already in progress for this business, skipping...');
    return ongoingFetches.get(fetchKey)!;
  }

  const fetchPromise = (async () => {
    try {
    // CRITICAL SAFETY CHECK: Verify that the business ID actually owns this phone number
    // This prevents messages from being stored with the wrong business ID due to race conditions
    const { data: business, error: businessError } = await supabase
      .from('businesses')
      .select('id, VITE_TWILIO_PHONE_NUMBER')
      .eq('id', settings.businessId)
      .single();

    if (businessError || !business) {
      console.error('❌ Business validation failed: Business not found', businessError);
      throw new Error(`Business ${settings.businessId} not found`);
    }

    // Normalize phone numbers for comparison
    const normalizedBusinessPhone = (business.VITE_TWILIO_PHONE_NUMBER || '').replace(/[^0-9]/g, '');
    const normalizedSettingsPhone = settings.phoneNumber.replace(/[^0-9]/g, '');

    if (normalizedBusinessPhone !== normalizedSettingsPhone) {
      console.error('❌ CRITICAL: Business ID mismatch detected!', {
        businessId: settings.businessId,
        businessPhone: business.VITE_TWILIO_PHONE_NUMBER,
        settingsPhone: settings.phoneNumber,
        message: 'This prevents messages from being stored with wrong business ID'
      });
      throw new Error(`Business ID ${settings.businessId} does not own phone number ${settings.phoneNumber}. Business owns ${business.VITE_TWILIO_PHONE_NUMBER}`);
    }

    console.log('✅ Business ID validation passed:', {
      businessId: settings.businessId,
      phoneNumber: settings.phoneNumber
    });

    // Get the latest message date from our database
    const { data: latestMessage } = await supabase
      .from('sms_messages')
      .select('created_at')
      .eq('business_id', settings.businessId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    // Format the date for Twilio's API (if we have a latest message)
    const dateFilter = latestMessage
      ? new Date(latestMessage.created_at).toISOString()
      : new Date(Date.now() - 60 * 60 * 1000).toISOString(); // Last 1 hour if no messages (reduced from 24 hours)

    // Fetch messages from Twilio (both inbound and outbound)
    const credentials = btoa(`${settings.accountSid}:${settings.authToken}`);
    // Query all messages and filter client-side to get both TO and FROM our number
    const response = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${settings.accountSid}/Messages.json?DateSent>=${dateFilter}`,
      {
        headers: {
          'Authorization': `Basic ${credentials}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch messages from Twilio');
    }

    const result = await response.json();
    
    // Filter out messages we already have
    const { data: existingMessages } = await supabase
      .from('sms_messages')
      .select('twilio_message_sid')
      .eq('business_id', settings.businessId);

    const existingSids = new Set(existingMessages?.map(m => m.twilio_message_sid) || []);

    // Store new messages (filter out duplicates from Twilio response too)
    // Also filter to only messages TO or FROM our phone number
    const normalizedPhoneNumber = settings.phoneNumber.replace(/[^0-9]/g, '');
    const seenSids = new Set<string>();
    const newMessages = result.messages.filter((msg: any) => {
      // Skip if already in database
      if (existingSids.has(msg.sid)) return false;
      // Skip if already seen in this batch (handle duplicates in Twilio response)
      if (seenSids.has(msg.sid)) return false;
      
      // Filter to only messages TO or FROM our phone number
      const msgTo = (msg.to || '').replace(/[^0-9]/g, '');
      const msgFrom = (msg.from || '').replace(/[^0-9]/g, '');
      const isRelevant = msgTo.includes(normalizedPhoneNumber) || msgFrom.includes(normalizedPhoneNumber);
      if (!isRelevant) return false;
      
      seenSids.add(msg.sid);
      return true;
    });

    if (newMessages.length > 0) {
      // Fetch media URLs for messages that have media
      const messagesWithMedia = await Promise.all(
        newMessages.map(async (msg: any) => {
          let mediaUrls: string[] = [];
          
          // If message has media, fetch the media URLs
          if (msg.num_media && parseInt(msg.num_media) > 0) {
            console.log(`📷 Message ${msg.sid} has ${msg.num_media} media file(s), fetching URLs...`);
            try {
              const mediaResponse = await fetch(
                `https://api.twilio.com/2010-04-01/Accounts/${settings.accountSid}/Messages/${msg.sid}/Media.json`,
                {
                  headers: {
                    'Authorization': `Basic ${credentials}`,
                  },
                }
              );

              if (mediaResponse.ok) {
                const mediaResult = await mediaResponse.json();
                console.log(`📷 Media response for ${msg.sid}:`, mediaResult);
                // Twilio API returns media in 'media_list' array (not 'media')
                const mediaArray = mediaResult.media_list || mediaResult.media || [];
                console.log(`📷 Found ${mediaArray.length} media item(s) in response`);
                // Twilio media URIs are relative paths, we need to construct the full URL
                // The media URI format is like: /2010-04-01/Accounts/{AccountSid}/Messages/{MessageSid}/Media/{MediaSid}
                // We can use the base Twilio API URL and append the URI
                mediaUrls = mediaArray.map((media: any) => {
                  // The URI from Twilio is like: /2010-04-01/Accounts/.../Media/...
                  // We need to construct the full URL for accessing the media
                  // For authenticated access, we'll store the full API path
                  const mediaUri = media.uri?.replace('.json', '') || '';
                  const fullUrl = `https://api.twilio.com${mediaUri}`;
                  console.log(`📷 Constructed media URL: ${fullUrl}`);
                  return fullUrl;
                });
                console.log(`📷 Fetched ${mediaUrls.length} media URL(s) for message ${msg.sid}`);
              } else {
                console.error(`❌ Failed to fetch media for ${msg.sid}:`, mediaResponse.status, mediaResponse.statusText);
              }
            } catch (mediaError) {
              console.error(`❌ Error fetching media for message ${msg.sid}:`, mediaError);
            }
          } else {
            console.log(`📱 Message ${msg.sid} has no media (num_media: ${msg.num_media})`);
          }

          return {
            business_id: settings.businessId,
            from_number: msg.from,
            to_number: msg.to,
            body: msg.body || '',  // Ensure body is never null (empty string for media-only messages)
            direction: msg.direction === 'inbound' ? 'inbound' : 'outbound',
            twilio_message_sid: msg.sid,
            media_urls: mediaUrls.length > 0 ? mediaUrls : null,  // Supabase will convert array to JSONB automatically
            created_at: new Date(msg.date_sent).toISOString(),
          };
        })
      );

      // Insert messages (trigger will prevent duplicates, but we also filter in code)
      // Log media URLs before inserting and ensure proper format
      messagesWithMedia.forEach((msg: any) => {
        if (msg.media_urls && Array.isArray(msg.media_urls) && msg.media_urls.length > 0) {
          console.log(`💾 Storing message ${msg.twilio_message_sid} with ${msg.media_urls.length} media URL(s):`, msg.media_urls);
          console.log(`💾 Media URLs type:`, typeof msg.media_urls, 'isArray:', Array.isArray(msg.media_urls));
        } else {
          console.log(`💾 Message ${msg.twilio_message_sid} has no media_urls (value:`, msg.media_urls, ')');
        }
      });

      // Separate messages into new inserts and updates
      // For messages with media URLs, check if they already exist and need updating
      const messagesToInsert: any[] = [];
      const messagesToUpdate: any[] = [];

      for (const msg of messagesWithMedia) {
        if (msg.media_urls && Array.isArray(msg.media_urls) && msg.media_urls.length > 0) {
          // Check if this message already exists
          const { data: existing } = await supabase
            .from('sms_messages')
            .select('id, twilio_message_sid, media_urls')
            .eq('twilio_message_sid', msg.twilio_message_sid)
            .single();

          if (existing) {
            // Message exists - check if it needs media URLs
            const existingMediaUrls = existing.media_urls;
            const hasNoMedia = !existingMediaUrls || 
                              (Array.isArray(existingMediaUrls) && existingMediaUrls.length === 0) ||
                              existingMediaUrls === null;
            
            if (hasNoMedia) {
              console.log(`🔄 Updating existing message ${msg.twilio_message_sid} with media URLs`);
              messagesToUpdate.push({
                id: existing.id,
                media_urls: msg.media_urls
              });
            } else {
              console.log(`⏭️ Message ${msg.twilio_message_sid} already has media URLs, skipping`);
            }
          } else {
            // New message - will be inserted
            messagesToInsert.push(msg);
          }
        } else {
          // No media URLs - just insert normally
          messagesToInsert.push(msg);
        }
      }

      // Insert new messages
      if (messagesToInsert.length > 0) {
        const { error: insertError, data: insertData } = await supabase
          .from('sms_messages')
          .insert(messagesToInsert)
          .select('id, twilio_message_sid, media_urls');

        if (insertData) {
          console.log('✅ Inserted messages:', insertData.map((m: any) => ({
            id: m.id,
            sid: m.twilio_message_sid,
            has_media: !!m.media_urls,
            media_count: Array.isArray(m.media_urls) ? m.media_urls.length : 0
          })));
        }

        if (insertError) {
          console.error('❌ Error inserting messages:', insertError);
        }
      }

      // Update existing messages with media URLs
      if (messagesToUpdate.length > 0) {
        for (const update of messagesToUpdate) {
          const { error: updateError, data: updateData } = await supabase
            .from('sms_messages')
            .update({ media_urls: update.media_urls })
            .eq('id', update.id)
            .select('id, twilio_message_sid, media_urls')
            .single();

          if (updateData) {
            console.log(`✅ Updated message ${updateData.twilio_message_sid} with media URLs:`, updateData.media_urls);
          }

          if (updateError) {
            console.error(`❌ Error updating message ${update.id}:`, updateError);
          }
        }
      }

      console.log(`✅ Processed ${messagesWithMedia.length} message(s): ${messagesToInsert.length} inserted, ${messagesToUpdate.length} updated`);
    }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      // Remove from ongoing fetches when done
      ongoingFetches.delete(fetchKey);
    }
  })();

  ongoingFetches.set(fetchKey, fetchPromise);
  return fetchPromise;
}

/**
 * Updates Twilio settings for a business
 */
export async function updateTwilioSettings({
  businessId,
  accountSid,
  authToken,
  phoneNumber,
  resourcePhoneNumbers = [],
  enabled,
}: TwilioSettings): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // If disabling Twilio, clear the settings
    if (!enabled) {
      const { error } = await supabase
        .from('businesses')
        .update({
          twilio_enabled: false,
          twilio_account_sid: '',
          twilio_auth_token: '',
          VITE_TWILIO_PHONE_NUMBER: '',
          VITE_TWILIO_RESOURCE_PHONE_NUMBERS: [],
          twilio_status: null,
          twilio_error_message: null
        })
        .eq('id', businessId);

      if (error) throw error;

      return {
        success: true,
      };
    }

    // Store credentials and immediately fetch messages
    const { error } = await supabase
      .from('businesses')
      .update({
        twilio_enabled: true,
        twilio_account_sid: accountSid,
        twilio_auth_token: authToken,
        VITE_TWILIO_PHONE_NUMBER: phoneNumber,
        VITE_TWILIO_RESOURCE_PHONE_NUMBERS: resourcePhoneNumbers,
        twilio_status: 'active',
        twilio_error_message: null
      })
      .eq('id', businessId);

    if (error) throw error;

    // Fetch initial messages
    await fetchAndStoreMessages({
      businessId,
      accountSid,
      authToken,
      phoneNumber: formatToE164(phoneNumber),
    });

    // Point this number at our inbound SMS webhook so future inbound SMS arrives
    // in real time. Non-fatal: connecting Twilio still succeeds if this fails.
    try {
      const webhookResult = await configureSmsWebhook(businessId);
      if (!webhookResult.success) {
        console.error('Twilio connect: SMS webhook configuration failed (non-fatal):', webhookResult.error);
      }
    } catch (webhookError) {
      console.error('Twilio connect: SMS webhook configuration error (non-fatal):', webhookError);
    }

    return {
      success: true,
    };

  } catch (error) {
    console.error('Error updating Twilio settings:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update Twilio settings',
    };
  }
}

/**
 * Points the business's Twilio number at our inbound SMS webhook (/sms-webhook),
 * via the configure-twilio-sms-webhook edge function. Sets ONLY the SMS webhook —
 * voice configuration is left untouched. Credentials are read server-side.
 */
export async function configureSmsWebhook(businessId: string): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) throw new Error('Supabase client is not initialized');

    const { data, error } = await supabase.functions.invoke('configure-twilio-sms-webhook', {
      body: { business_id: businessId },
    });

    if (error) {
      return { success: false, error: error.message };
    }
    if (!data?.success) {
      return { success: false, error: data?.error || 'Failed to configure SMS webhook' };
    }
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to configure SMS webhook',
    };
  }
}

/**
 * Gets Twilio settings for a business
 */
export async function getTwilioSettings(businessId: string): Promise<{ success: boolean; settings?: any; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    const { data: settings, error } = await supabase
      .from('businesses')
      .select('twilio_enabled, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER, VITE_TWILIO_RESOURCE_PHONE_NUMBERS, twilio_status, twilio_error_message, twilio_call_forwarding_enabled, twilio_forwarding_number, twilio_webhooks_configured, twilio_webhook_configured_at')
      .eq('id', businessId)
      .single();

    if (error) throw error;

    return {
      success: true,
      settings: settings,
    };
  } catch (error) {
    console.error('Error getting Twilio settings:', error);
    return {
      success: false,
      error: 'Failed to get Twilio settings',
    };
  }
}

/**
 * Sends a test SMS message using the stored Twilio credentials
 */
export async function sendTestSMS(
  businessId: string, 
  toPhoneNumber: string,
  customMessage?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // Get the business's Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER')
      .eq('id', businessId)
      .single();

    if (settingsError || !settings?.twilio_account_sid || !settings?.twilio_auth_token || !settings?.VITE_TWILIO_PHONE_NUMBER) {
      return {
        success: false,
        error: 'Twilio credentials not found. Please save your Twilio settings first.',
      };
    }

    // Format phone numbers to E.164 format
    const formattedToNumber = formatToE164(toPhoneNumber);
    const formattedFromNumber = formatToE164(settings.VITE_TWILIO_PHONE_NUMBER);
    
    // Use custom message if provided, otherwise use default test message
    const messageBody = customMessage || `This is a test message from your booking system. 📱\n\nTo test if this number can receive messages, please reply "TEST" to this message.\n\nTwilio number: ${formattedFromNumber}`;

    // Send test message using Twilio API
    const credentials = btoa(`${settings.twilio_account_sid}:${settings.twilio_auth_token}`);
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${settings.twilio_account_sid}/Messages.json`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        To: formattedToNumber,
        From: formattedFromNumber,
        Body: messageBody,
      }).toString(),
    });

    const result = await response.json();
    
    if (!response.ok || result.error_code || result.error_message) {
      return {
        success: false,
        error: result.error_message || result.message || 'Failed to send test message',
      };
    }

    // Store the outbound message in the database
    const { error: insertError } = await supabase
      .from('sms_messages')
      .insert({
        business_id: businessId,
        from_number: formattedFromNumber,
        to_number: formattedToNumber,
        body: messageBody,
        direction: 'outbound',
        twilio_message_sid: result.sid,
        created_at: new Date().toISOString(),
      });

    if (insertError) {
      console.error('Error storing outbound message:', insertError);
    }

    // Fetch any new messages (including the one we just sent)
    await fetchAndStoreMessages({
      businessId,
      accountSid: settings.twilio_account_sid,
      authToken: settings.twilio_auth_token,
      phoneNumber: formattedFromNumber,
    });

    return {
      success: true,
    };

  } catch (error) {
    console.error('Error sending test SMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send test message',
    };
  }
}

/**
 * Sends an SMS message using test credentials
 */
export async function sendSMSWithTestCredentials(
  businessId: string,
  toPhoneNumber: string,
  message: string,
  aiGenerated: boolean = false,
  mediaFiles?: File[],
  customerId?: string | null
): Promise<{ success: boolean; error?: string; message?: any }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // Get the business's Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER, twilio_connection_method')
      .eq('id', businessId)
      .single();

    const isConnect = settings?.twilio_connection_method === 'connect';
    // Connect businesses authenticate server-side with the platform master token,
    // so they don't store (or need) their own auth token client-side.
    const missingCreds = !settings?.twilio_account_sid || !settings?.VITE_TWILIO_PHONE_NUMBER ||
      (!isConnect && !settings?.twilio_auth_token);
    if (settingsError || missingCreds) {
      console.error('Twilio settings error:', settingsError || 'Missing credentials');
      return {
        success: false,
        error: 'Twilio credentials not found. Please save your Twilio settings first.',
      };
    }

    // Format phone numbers to E.164 format
    const formattedToNumber = formatToE164(toPhoneNumber);
    const formattedFromNumber = formatToE164(settings.VITE_TWILIO_PHONE_NUMBER);

    console.log('Sending SMS with settings:', {
      to: formattedToNumber,
      from: formattedFromNumber,
      hasAccountSid: !!settings.twilio_account_sid,
      hasAuthToken: !!settings.twilio_auth_token,
      messageLength: message.length,
      mediaCount: mediaFiles?.length || 0
    });

    // If we have media files, upload them to Supabase Storage first to get public URLs
    let mediaUrls: string[] = [];
    if (mediaFiles && mediaFiles.length > 0) {
      console.log(`📷 Uploading ${mediaFiles.length} media file(s) to storage...`);
      try {
        // Upload each file to Supabase Storage
        const uploadPromises = mediaFiles.map(async (file, index) => {
          const fileExt = file.name.split('.').pop() || 'jpg';
          const fileName = `sms-media/${businessId}/${Date.now()}_${index}_${Math.random().toString(36).substring(7)}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('user-uploads')
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false,
              // Some platforms (iOS camera capture) hand us a File with an empty
              // mime type; default to JPEG so Twilio MMS gets a valid content type.
              contentType: file.type || 'image/jpeg'
            });

          if (uploadError) {
            console.error(`❌ Failed to upload media file ${index}:`, uploadError);
            throw new Error(`Failed to upload media: ${uploadError.message}`);
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('user-uploads')
            .getPublicUrl(fileName);

          console.log(`✅ Uploaded media ${index + 1}/${mediaFiles.length}: ${publicUrl}`);
          return publicUrl;
        });

        mediaUrls = await Promise.all(uploadPromises);
        console.log(`✅ All ${mediaUrls.length} media file(s) uploaded successfully`);
      } catch (uploadError) {
        console.error('❌ Error uploading media files:', uploadError);
        return {
          success: false,
          error: uploadError instanceof Error ? uploadError.message : 'Failed to upload media files'
        };
      }
    }

    // Send message. Connect businesses go through a server-side edge function so
    // the platform master token never reaches the browser; manual businesses call
    // the Twilio API directly with their own stored token.
    let result: any;
    if (isConnect) {
      const { data: sendData, error: sendError } = await supabase.functions.invoke('twilio-connect-send', {
        body: {
          businessId,
          to: formattedToNumber,
          from: formattedFromNumber,
          body: message.trim() ? message : '',
          mediaUrls,
        },
      });
      if (sendError || !sendData?.success) {
        const errMsg = sendData?.error || sendError?.message || 'Failed to send message';
        console.error('Connect send error:', errMsg);
        return { success: false, error: errMsg };
      }
      result = { sid: sendData.sid, status: sendData.status };
    } else {
      const credentials = btoa(`${settings.twilio_account_sid}:${settings.twilio_auth_token}`);

      // Build request body - if we have media URLs, include them for MMS
      const params = new URLSearchParams({
        To: formattedToNumber,
        From: formattedFromNumber,
      });

      if (message.trim()) {
        params.append('Body', message);
      }

      // Add media URLs for MMS
      mediaUrls.forEach(url => {
        params.append('MediaUrl', url);
      });

      const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${settings.twilio_account_sid}/Messages.json`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      result = await response.json();

      if (!response.ok || result.error_code || result.error_message) {
        console.error('Twilio API error:', result);
        return {
          success: false,
          error: result.error_message || result.message || 'Failed to send message',
        };
      }
    }

    // Use the Supabase storage URLs we already uploaded as the canonical media_urls.
    // Fetching from Twilio's Media subresource immediately after send is unreliable
    // because Twilio processes media asynchronously.
    const storedMediaUrls = mediaUrls.length > 0 ? mediaUrls : [];
    
    // Persist the outbound message via sms_messages — the SAME proven path the
    // inbound webhook and "send test SMS" use. The copy_sms_to_all_messages
    // trigger then creates the all_messages row with the correct schema (status,
    // message_status, metadata, read, etc.), so it can't silently fail on a
    // missing/NOT NULL column the way a direct all_messages insert could.
    // (The message poller used to back-fill outbound from Twilio; now this must
    // persist or the sent message vanishes on reopen.)
    let insertedMessage: any = null;
    if (supabase) {
      // Core row carries the customer and who sent it. When the conversation's
      // customer is known it wins; otherwise the insert trigger matches by phone.
      const sender = await resolveCurrentSender(businessId);
      const { error: smsError } = await supabase
        .from('sms_messages')
        .insert({
          business_id: businessId,
          from_number: formattedFromNumber,
          to_number: formattedToNumber,
          body: message,
          direction: 'outbound',
          twilio_message_sid: result.sid,
          media_urls: storedMediaUrls.length > 0 ? storedMediaUrls : null,
          created_at: new Date().toISOString(),
          customer_id: customerId || null,
          ...senderColumns(sender),
        });

      if (smsError) {
        console.error('Error storing outbound SMS:', smsError);
      } else {
        // The trigger has created the all_messages row in the same transaction —
        // read it back so the UI can replace the optimistic bubble with the real row.
        const { data: allMsg } = await supabase
          .from('all_messages')
          .select('*')
          .eq('twilio_message_sid', result.sid)
          .eq('direction', 'outbound')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // The legacy row has no sender columns; carry the stamp with it so the
        // bubble shows the right avatar the moment it lands.
        insertedMessage = allMsg ? { ...allMsg, sender } : null;

        // When we KNOW the conversation's customer, force the message onto that
        // customer — never trust the trigger's phone-match, which can attach to a
        // different (e.g. duplicate) customer record and split the thread.
        if (allMsg && customerId && allMsg.customer_id !== customerId) {
          await supabase.from('all_messages').update({ customer_id: customerId }).eq('id', allMsg.id);
          insertedMessage = { ...allMsg, customer_id: customerId, sender };
        }
      }
    }

    console.log('SMS sent successfully');
    return { success: true, message: insertedMessage };

  } catch (error) {
    console.error('Error in sendSMSWithTestCredentials:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send message'
    };
  }
}

export async function validateTwilioSettingsForBusiness(businessId: string): Promise<{ 
  isValid: boolean; 
  error?: string;
  settings?: {
    twilio_enabled: boolean;
    twilio_status: string | null;
    twilio_account_sid: string | null;
    twilio_auth_token: string | null;
    VITE_TWILIO_PHONE_NUMBER: string | null;
  };
}> {
  try {
    if (!supabase) {
      return { isValid: false, error: 'Supabase client is not initialized' };
    }

    // Get the business's Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_enabled, twilio_status, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER')
      .eq('id', businessId)
      .single();

    if (settingsError) {
      console.error('Error fetching Twilio settings:', settingsError);
      return { isValid: false, error: 'Failed to fetch Twilio settings' };
    }

    if (!settings) {
      return { isValid: false, error: 'Business not found' };
    }

    // Check if Twilio is enabled
    if (!settings.twilio_enabled) {
      return { isValid: false, error: 'Twilio is not enabled for this business', settings };
    }

    // Check if Twilio status is active
    if (settings.twilio_status !== 'active') {
      return { isValid: false, error: 'Twilio integration is not active', settings };
    }

    // Check if all required credentials are present
    if (!settings.twilio_account_sid || !settings.twilio_auth_token || !settings.VITE_TWILIO_PHONE_NUMBER) {
      return { isValid: false, error: 'Missing Twilio credentials', settings };
    }

    return { isValid: true, settings };
  } catch (error) {
    console.error('Error validating Twilio settings:', error);
    return { isValid: false, error: 'Failed to validate Twilio settings' };
  }
}

export async function sendSMS({ businessId, to, message }: { businessId: string; to: string; message: string }): Promise<{ success: boolean; error?: string }> {
  try {
    // First validate Twilio settings
    const validation = await validateTwilioSettingsForBusiness(businessId);
    if (!validation.isValid) {
      console.error('Twilio validation failed:', validation.error);
      return { success: false, error: validation.error };
    }

    const settings = validation.settings!;

    // Format phone numbers to E.164 format
    const formattedToNumber = formatToE164(to);
    const formattedFromNumber = formatToE164(settings.VITE_TWILIO_PHONE_NUMBER!);

    console.log('Sending SMS with settings:', {
      to: formattedToNumber,
      from: formattedFromNumber,
      hasAccountSid: !!settings.twilio_account_sid,
      hasAuthToken: !!settings.twilio_auth_token,
      messageLength: message.length
    });

    // Send message using Twilio API
    const credentials = btoa(`${settings.twilio_account_sid}:${settings.twilio_auth_token}`);
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${settings.twilio_account_sid}/Messages.json`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        To: formattedToNumber,
        From: formattedFromNumber,
        Body: message,
      }).toString(),
    });

    const result = await response.json();
    
    if (!response.ok || result.error_code || result.error_message) {
      console.error('Twilio API error:', result);
      return {
        success: false,
        error: result.error_message || result.message || 'Failed to send message',
      };
    }

    // Fetch media URLs if message has media
    let mediaUrls: string[] = [];
    if (result.num_media && parseInt(result.num_media) > 0) {
      try {
        const mediaResponse = await fetch(
          `https://api.twilio.com/2010-04-01/Accounts/${settings.twilio_account_sid}/Messages/${result.sid}/Media.json`,
          {
            headers: {
              'Authorization': `Basic ${credentials}`,
            },
          }
        );

        if (mediaResponse.ok) {
          const mediaResult = await mediaResponse.json();
          mediaUrls = (mediaResult.media || []).map((media: any) => {
            const mediaUri = media.uri?.replace('.json', '') || '';
            return `https://api.twilio.com${mediaUri}`;
          });
        }
      } catch (mediaError) {
        console.error(`Error fetching media for sent message ${result.sid}:`, mediaError);
      }
    }

    // Store the outbound message in the database
    if (supabase) {
      const { error: insertError } = await supabase
        .from('sms_messages')
        .insert({
          business_id: businessId,
          from_number: formattedFromNumber,
          to_number: formattedToNumber,
          body: message,
          direction: 'outbound',
          twilio_message_sid: result.sid,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null,
          created_at: new Date().toISOString(),
        });

      if (insertError) {
        console.error('Error storing outbound message:', insertError);
      }
    }

    console.log('SMS sent successfully');
    return { success: true };

  } catch (error) {
    console.error('Error in sendSMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send message'
    };
  }
}

export async function validateTwilioClient(accountSid: string, authToken: string, phoneNumber: string): Promise<{ isValid: boolean; error?: string }> {
  try {
    const response = await fetch('/api/twilio/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accountSid,
        authToken,
        phoneNumber,
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      return { 
        isValid: false, 
        error: result.error || 'Failed to validate Twilio credentials' 
      };
    }

    return result;
  } catch (error) {
    console.error('Error validating Twilio client:', error);
    return { 
      isValid: false, 
      error: 'Failed to validate Twilio credentials' 
    };
  }
}

/**
 * Updates Twilio call forwarding settings
 */
export async function updateCallForwardingSettings({
  businessId,
  callForwardingEnabled,
  forwardingNumber
}: {
  businessId: string;
  callForwardingEnabled: boolean;
  forwardingNumber: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // First get the current Twilio settings
    const { data: settings, error: settingsError } = await supabase
      .from('businesses')
      .select('twilio_enabled, twilio_status')
      .eq('id', businessId)
      .single();

    if (settingsError) {
      throw new Error('Failed to get current Twilio settings');
    }

    if (!settings.twilio_enabled || settings.twilio_status !== 'active') {
      return {
        success: false,
        error: 'Twilio must be enabled and active to configure call forwarding'
      };
    }

    // Update call forwarding settings
    const { error } = await supabase
      .from('businesses')
      .update({
        twilio_call_forwarding_enabled: callForwardingEnabled,
        twilio_forwarding_number: callForwardingEnabled ? formatToE164(forwardingNumber) : null
      })
      .eq('id', businessId);

    if (error) throw error;

    return {
      success: true
    };
  } catch (error) {
    console.error('Error updating call forwarding settings:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update call forwarding settings'
    };
  }
}

/**
 * Automatically configure Twilio webhooks for a business
 */
export async function configureTwilioWebhooks({
  businessId,
  accountSid,
  authToken,
  phoneNumber
}: {
  businessId: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
}): Promise<{ success: boolean; error?: string; voiceUrl?: string; statusCallbackUrl?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    // Get the current session for authentication
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available');
    }

    console.log('🔧 Configuring Twilio webhooks automatically...');

    // Call our edge function to configure the webhooks using Supabase client
    const { data: result, error: functionError } = await supabase.functions.invoke('configure-twilio-webhooks', {
      body: {
        business_id: businessId,
        account_sid: accountSid,
        auth_token: authToken,
        phone_number: phoneNumber
      }
    });

    if (functionError) {
      console.error('❌ Failed to configure webhooks:', functionError);
      return {
        success: false,
        error: functionError.message || 'Failed to configure Twilio webhooks'
      };
    }

    if (!result || !result.success) {
      console.error('❌ Failed to configure webhooks:', result);
      return {
        success: false,
        error: result?.error || 'Failed to configure Twilio webhooks'
      };
    }

    console.log('✅ Twilio webhooks configured successfully');
    return {
      success: true,
      voiceUrl: result.voiceUrl,
      statusCallbackUrl: result.statusCallbackUrl
    };
  } catch (error) {
    console.error('Error configuring Twilio webhooks:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to configure Twilio webhooks'
    };
  }
}

/**
 * Disconnect and reset Twilio integration for a business
 */
export async function disconnectTwilio({
  businessId
}: {
  businessId: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    console.log('🔌 Disconnecting Twilio integration...');

    // Reset all Twilio-related fields in the database
    const { error } = await supabase
      .from('businesses')
      .update({
        twilio_enabled: false,
        twilio_account_sid: null,
        twilio_auth_token: null,
        VITE_TWILIO_PHONE_NUMBER: null,
        twilio_status: null,
        twilio_error_message: null,
        twilio_call_forwarding_enabled: false,
        twilio_forwarding_number: null,
        twilio_webhooks_configured: false,
        twilio_webhook_configured_at: null,
        twilio_application_sid: null,
        // Clear the cached Voice API key so a new account doesn't reuse a stale one.
        twilio_api_key_sid: null,
        twilio_api_key_secret: null,
        // Reset connection method + provisioned number so the business can
        // reconnect cleanly via either method.
        twilio_connection_method: 'manual',
        twilio_phone_number_sid: null
      })
      .eq('id', businessId);

    if (error) throw error;

    console.log('✅ Twilio integration disconnected successfully');
    return {
      success: true
    };
  } catch (error) {
    console.error('Error disconnecting Twilio:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to disconnect Twilio'
    };
  }
}